import { useAuthCookie } from '~/composables/useAuthCookie';
import { COOKIE_NAME_DOCEBO_ACCESS_TOKEN } from '~/types/cookieNames';
import log from '~/utils/log';

/**
 * The Docebo guide recommends to dynamically retrieve this URL
 * from the `/setup/v1/flow/settings` endpoint. However, whilst
 * discussing this with our Docebo technical contact, they confirmed
 * and recommended against this approach. Instead, we should just
 * hardcode the URL.
 *
 * Otherwise, it could happen that the SDK may introduce breaking changes
 * which need to be manually verified by us before we can update the URL.
 * Also, they confirmed that the URL is not expected to change often, and
 * if it does, they will inform us in advance.
 */
const DSDK_SCRIPT_SRC = 'https://cdn1.dcbstatic.com/flow/kernels/SDKKernel-v1-latest.bundle.min.js';
const DSDK_SCRIPT_KEY = 'docebo_embedded_learning';

const DEFAULT_DSDK_LOAD_TIMEOUT_MS = 60_000;
const DSDK_POLL_INTERVAL_MS = 50;

let startPromise: Promise<void> | null = null;
const isReady = ref(false);
const isStarting = ref(false);
const hasError = ref(false);

const waitForDsdk = async (timeoutMs: number) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt <= timeoutMs) {
        if (typeof DSDK !== 'undefined') return true;
        await new Promise((resolve) => setTimeout(resolve, DSDK_POLL_INTERVAL_MS));
    }
    return false;
};

export const useDoceboKernel = () => {
    const runtimeConfig = useRuntimeConfig();
    const accessTokenCookie = useAuthCookie(COOKIE_NAME_DOCEBO_ACCESS_TOKEN);

    useHead({
        script: [
            {
                key: DSDK_SCRIPT_KEY,
                src: DSDK_SCRIPT_SRC,
                defer: true
            }
        ]
    });

    const start = (timeoutMs = DEFAULT_DSDK_LOAD_TIMEOUT_MS): Promise<void> => {
        if (startPromise) return startPromise;
        if (!import.meta.client) return Promise.resolve();

        isStarting.value = true;
        hasError.value = false;

        startPromise = (async () => {
            const loaded = await waitForDsdk(timeoutMs);
            if (!loaded || typeof DSDK === 'undefined') {
                log.error('[useDoceboKernel] DSDK script did not load in time');
                throw new Error('DSDK script did not load in time');
            }

            const tokenProvider = async () => accessTokenCookie.value;

            DSDK.kernel().setTokenProvider(tokenProvider);
            await DSDK.kernel().start({ domain: runtimeConfig.public.docebo.domain });

            isReady.value = true;
        })()
            .catch((error: unknown) => {
                hasError.value = true;
                startPromise = null;
                log.error('[useDoceboKernel] Failed to start Docebo kernel', error);
                throw error;
            })
            .finally(() => {
                isStarting.value = false;
            });

        return startPromise;
    };

    const whenReady = (timeoutMs?: number): Promise<void> => {
        const pending = startPromise ?? start(timeoutMs);
        return pending;
    };

    return {
        isReady: readonly(isReady),
        isStarting: readonly(isStarting),
        hasError: readonly(hasError),
        start,
        whenReady
    };
};
